'use client';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextareaAutosize,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { LayoutQuestion } from '../LayoutQuestion';

import styles from '../../Form/Form.module.css';
export default function MediaQuestion(props) {
  const { enqueueSnackbar } = useSnackbar();
  const getActiveMediaType = () => {
    const type = props.information.multimedia?.type;
    return ['youtube', 'image', 'video'].includes(type) ? type : null;
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type)) {
      enqueueSnackbar(
        'Solo se permiten imágenes en formato JPG, PNG, GIF o WEBP.',
        {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          autoHideDuration: 3000, // en milisegundos (3 segundos)
        }
      );
      return;
    }

    const maxSizeMB = 5;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      enqueueSnackbar(`La imagen supera el límite de ${maxSizeMB}MB.`, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        autoHideDuration: 3000, // en milisegundos (3 segundos)
      });
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    props.setInformation((prev) => ({
      ...prev,
      multimedia: {
        type: 'image',
        urlMedia: imageUrl,
        fileName: file.name,
      },
    }));
  };

  const handleVideoUpload = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const videoFile = files[0];

      // ✅ Validar tipo MIME permitido
      const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg'];
      if (!allowedTypes.includes(videoFile.type)) {
        enqueueSnackbar('Solo se permiten videos en formato MP4, WebM u OGG.', {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          autoHideDuration: 3000, // en milisegundos (3 segundos)
        });
        return;
      }

      // ✅ Validar tamaño
      const maxSizeMB = 50;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      if (videoFile.size > maxSizeBytes) {
        enqueueSnackbar(`El video supera el límite de ${maxSizeMB}MB.`, {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          autoHideDuration: 3000, // en milisegundos (3 segundos)
        });
        return;
      }

      // ✅ Procesar video
      const videoUrl = URL.createObjectURL(videoFile);
      props.setInformation((prev) => ({
        ...prev,
        multimedia: {
          type: 'video',
          urlMedia: videoUrl,
          fileName: videoFile.name,
        },
      }));
    }
  };

  const clearMultimedia = () => {
    props.setInformation((prev) => ({
      ...prev,
      multimedia: {
        type: '',
        urlMedia: '',
        fileName: '',
      },
    }));
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Card elevation={2} sx={{ borderRadius: 3, overflow: 'visible' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            {/* Pregunta Principal */}
            <LayoutQuestion {...props} />
            {/* Error de multimedia */}
            {props.errorMessage.multimedia?.general &&
              props.information.multimedia.type !== 'youtube' && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {props.helperText.multimedia?.general ||
                    'Error en multimedia'}
                </Alert>
              )}

            {/* YouTube URL */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <PlayArrowIcon sx={{ color: '#FF0000', fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Video de YouTube
                </Typography>
              </Box>
              <TextField
                placeholder="https://www.youtube.com/watch?v=..."
                value={props.information.youtubeUrl}
                name="youtubeUrl"
                onChange={props.handleInformation}
                error={
                  !!props.errorMessage.youtubeUrl ||
                  (props.information.multimedia?.type === 'youtube' &&
                    !!props.errorMessage.multimedia?.youtube)
                }
                helperText={
                  props.helperText.youtubeUrl ||
                  (props.information.multimedia?.type === 'youtube' &&
                  props.errorMessage.multimedia?.youtube
                    ? props.helperText.multimedia?.youtubeMessage ?? ''
                    : '')
                }
                fullWidth
                disabled={
                  getActiveMediaType() === 'image' ||
                  getActiveMediaType() === 'video'
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlayArrowIcon sx={{ color: '#FF0000' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor:
                      getActiveMediaType() === 'image' ||
                      getActiveMediaType() === 'video'
                        ? 'grey.100'
                        : 'white',
                  },
                }}
              />
            </Paper>

            {/* Imágenes */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <ImageIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Imágenes
                </Typography>
              </Box>

              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                hidden
                id="image-upload"
                disabled={
                  getActiveMediaType() === 'youtube' ||
                  getActiveMediaType() === 'video'
                }
              />

              <label htmlFor="image-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={
                    getActiveMediaType() === 'youtube' ||
                    getActiveMediaType() === 'video'
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  Subir Imagen (máx. 5MB)
                </Button>
              </label>

              {props.information.multimedia?.type === 'image' &&
                props.information.multimedia?.urlMedia && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={
                        props.information.multimedia.fileName || 'imagen subida'
                      }
                      onDelete={clearMultimedia}
                      deleteIcon={<CloseIcon />}
                      variant="filled"
                      color="primary"
                      sx={{
                        maxWidth: 300,
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                )}
            </Paper>

            {/* Videos */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <VideocamIcon color="secondary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Videos
                </Typography>
              </Box>

              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                hidden
                id="video-upload"
                disabled={
                  getActiveMediaType() === 'youtube' ||
                  getActiveMediaType() === 'image'
                }
              />

              <label htmlFor="video-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  color="secondary"
                  disabled={
                    getActiveMediaType() === 'youtube' ||
                    getActiveMediaType() === 'image'
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                  }}
                >
                  Subir Video (máx. 50MB)
                </Button>
              </label>

              {props.information.multimedia?.type === 'video' &&
                props.information.multimedia?.urlMedia && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      label={
                        props.information.multimedia.fileName || 'video subido'
                      }
                      onDelete={clearMultimedia}
                      deleteIcon={<CloseIcon />}
                      variant="filled"
                      color="secondary"
                      sx={{
                        maxWidth: 300,
                        '& .MuiChip-label': {
                          fontWeight: 500,
                        },
                      }}
                    />
                  </Box>
                )}
            </Paper>

            {/* Información adicional */}
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> Solo puedes seleccionar un tipo de
                multimedia por pregunta. Los campos se deshabilitarán
                automáticamente cuando selecciones una opción.
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
